<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
import { Slideover } from "@/components/Base/Headless";
import { FormSelect } from "@/components/Base/Form";
import TomSelect from "@/components/Base/TomSelect";
import Lucide from "@/components/Base/Lucide";
import api from "@/utils/axios";
import { useAuthStore } from "@/stores/auth";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";

dayjs.extend(relativeTime);
dayjs.locale("es");

const { t } = useI18n();

interface Activity {
  id: number;
  user_id: number;
  document_id: number;
  document_title: string;
  module: string;
  accessed_at: string;
  user_name?: string;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
}

interface MainProps {
  activitiesPanel: boolean;
  setActivitiesPanel: (val: boolean) => void;
}

const props = withDefaults(defineProps<MainProps>(), {
  activitiesPanel: false,
  setActivitiesPanel: () => {},
});

const authStore = useAuthStore();
const isManagement = computed(() => ["ADMIN", "TECNICO"].includes(authStore.user?.role));
const isAdmin = authStore.user?.role === "ADMIN";
const activities = ref<Activity[]>([]);
const users = ref<User[]>([]);
const selectedUserId = ref<string>("");
const loading = ref(false);

const fetchActivities = async () => {
  loading.value = true;
  try {
    const params = selectedUserId.value ? { user_id: selectedUserId.value } : {};
    const response = await api.get("/system/latest-activities", { 
      params: params 
    });
    if (response.data.status === "success") {
      activities.value = response.data.data;
    }
  } catch (error) {
    console.error("Error fetching activities:", error);
  } finally {
    loading.value = false;
  }
};

const fetchUsers = async () => {
  if (!isManagement.value) return;
  try {
    const response = await api.get("/users");

    // Standard response format in this project seems to be { status, data: [...] }
    if (response.data.status === 'success') {
      users.value = response.data.data;
    } else if (Array.isArray(response.data)) {
      users.value = response.data;
    }

    // Sort alphabetically
    users.value.sort((a, b) => {
      const nameA = (`${a.first_name} ${a.last_name}`).toLowerCase();
      const nameB = (`${b.first_name} ${b.last_name}`).toLowerCase();
      return nameA.localeCompare(nameB, "es");
    });
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

const getIcon = (module: string) => {
  switch (module) {
    case "LOANS": return "Book";
    case "REPRODUCTIONS": return "Camera";
    case "PUBLICATIONS": return "FileText";
    case "DOCUMENTS": return "Archive";
    case "FILES": return "File";
    case "EXPEDITIONS": return "Map";
    case "INCIDENTS": return "LifeBuoy";
    default: return "File";

  }
};

const getModuleLabel = (module: string) => {
  switch (module) {
    case "LOANS": return t("activities.loan");
    case "REPRODUCTIONS": return t("activities.reproduction");
    case "PUBLICATIONS": return t("activities.publication");
    case "DOCUMENTS": return t("activities.document");
    case "FILES": return t("activities.file");
    case "EXPEDITIONS": return t("activities.mission");
    case "INCIDENTS": return t("activities.incident");
    default: return module;
  }
};

const getModuleClass = (module: string) => {
  switch (module) {
    case "LOANS": return "bg-primary/10 text-primary border-primary/20";
    case "REPRODUCTIONS": return "bg-success/10 text-success border-success/20";
    case "PUBLICATIONS": return "bg-warning/10 text-warning border-warning/20";
    case "DOCUMENTS": return "bg-slate-100 text-slate-600 border-slate-200";
    case "FILES": return "bg-slate-100 text-slate-500 border-slate-200";
    case "EXPEDITIONS": return "bg-info/10 text-info border-info/20";
    case "INCIDENTS": return "bg-danger/10 text-danger border-danger/20";
    default: return "bg-slate-100 text-slate-500 border-slate-200";

  }
};

const formatTime = (date: string) => {
  return dayjs(date).fromNow();
};

const getLink = (item: Activity) => {
  const module = item.module.toLowerCase();
  return `/${module}/${item.document_id}`;
};

onMounted(() => {
  if (props.activitiesPanel) {
    fetchActivities();
    fetchUsers();
  }
});

watch(() => props.activitiesPanel, (val) => {
  if (val) {
    fetchActivities();
    fetchUsers();
  }
});

watch(selectedUserId, () => {
  fetchActivities();
});
</script>

<template>
  <div>
    <Slideover
      :open="props.activitiesPanel"
      @close="() => props.setActivitiesPanel(false)"
    >
      <Slideover.Panel class="w-80 sm:w-96 rounded-[0.75rem_0_0_0.75rem]">
        <a
          href=""
          class="focus:outline-none hover:bg-white/10 bg-white/5 transition-all hover:rotate-180 absolute inset-y-0 left-0 right-auto flex items-center justify-center my-auto -ml-[60px] sm:-ml-[105px] border rounded-full text-white/90 w-8 h-8 sm:w-14 sm:h-14 border-white/90 hover:scale-105"
          @click.prevent="props.setActivitiesPanel(false)"
        >
          <Lucide class="w-8 h-8 stroke-[1]" icon="X" />
        </a>
        <Slideover.Title class="px-6 py-5 flex items-center justify-between">
          <h2 class="text-base font-semibold">{{ $t('activities.title') }}</h2>
          <div v-if="loading" class="animate-spin text-slate-400">
             <Lucide icon="Loader2" class="w-4 h-4" />
          </div>
        </Slideover.Title>
        <Slideover.Description class="p-0 flex flex-col h-full bg-white overflow-hidden">
          <!-- Filtro Gestión -->
          <div v-if="isManagement" class="p-4 border-b border-slate-100 bg-slate-50/50">
            <label class="text-xs font-medium text-slate-500 mb-1.5 block uppercase tracking-wider">{{ $t('activities.filter_by_user') }}</label>
            <TomSelect 
              v-model="selectedUserId" 
              class="w-full text-sm"
              :options="{
                placeholder: $t('activities.select_user'),
                plugins: {
                  dropdown_input: {}
                }
              }"
            >
              <option value="">{{ $t('activities.all_users') }}</option>
              <option v-for="u in users" :key="u.id" :value="u.id.toString()">
                {{ u.first_name }} {{ u.last_name }}
              </option>
            </TomSelect>
          </div>

          <!-- Lista de Actividades -->
          <div class="flex-1 overflow-y-auto p-4 custom-scrollbar">
            <div v-if="activities.length > 0" class="space-y-4">
              <div
                v-for="item in activities"
                :key="item.id"
                class="group relative flex gap-3 p-3 rounded-xl hover:bg-slate-50 transition-all border border-transparent hover:border-slate-100"
              >
                <!-- Icono/Avatar del módulo -->
                <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border" :class="getModuleClass(item.module)">
                  <Lucide :icon="getIcon(item.module)" class="w-5 h-5" />
                </div>

                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between gap-2 mb-0.5">
                    <span class="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      {{ getModuleLabel(item.module) }}
                    </span>
                    <span class="text-[10px] text-slate-400 italic">
                      {{ formatTime(item.accessed_at) }}
                    </span>
                  </div>
                  
                  <router-link
                    :to="getLink(item)"
                    class="block text-sm font-semibold text-slate-900 truncate group-hover:text-primary transition-colors"
                    @click="props.setActivitiesPanel(false)"
                  >
                    {{ item.document_title }}
                  </router-link>

                  <div v-if="isManagement && item.user_name" class="mt-1 flex items-center text-[11px] text-slate-500 font-medium">
                    <Lucide icon="User" class="w-3 h-3 mr-1 text-slate-400" />
                    {{ item.user_name }}
                  </div>
                </div>

                <!-- Botón Ir al Documento -->
                <div class="flex-shrink-0 flex items-center">
                  <router-link
                    :to="getLink(item)"
                    class="flex items-center justify-center w-8 h-8 rounded-full border border-slate-200 text-slate-500 hover:bg-primary hover:text-white hover:border-primary transition-all shadow-sm"
                    @click="props.setActivitiesPanel(false)"
                    :title="$t('activities.go_to_document')"
                  >
                    <Lucide icon="Eye" class="w-4 h-4" />
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Estado Vacío -->
            <div v-else-if="!loading" class="flex flex-col items-center justify-center h-64 text-center px-6">
              <div class="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-300">
                <Lucide icon="Activity" class="w-8 h-8" />
              </div>
              <p class="text-sm font-medium text-slate-600">
                {{ $t('activities.empty_title') }}
              </p>
              <p class="text-xs text-slate-400 mt-1">
                {{ $t('activities.empty_desc') }}
              </p>
            </div>
          </div>
        </Slideover.Description>
      </Slideover.Panel>
    </Slideover>
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}
</style>
